import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import type {
  GetRecentActivitiesApiResponse,
  RecentActivityListItemApiResponse,
} from "@/types/api/recent-activities";

export interface PromiseUnifiedActivity {
  id: string;
  type:
    | "email"
    | "call"
    | "sms"
    | "note"
    | "promise"
    | "payment"
    | "status_change"
    | "communication"
    | "case_note"
    | "system"
    | "case"
    | "dispute";
  tag: "Activity" | "Note" | "Communication";
  title: string;
  description: string;
  outcome?: string;
  createdBy: string;
  createdAt: Date;
}

const ACTIVITY_TYPE_TO_TAG: Record<string, PromiseUnifiedActivity["tag"]> = {
  note: "Note",
  email: "Communication",
  phone: "Communication",
  sms: "Communication",
  communication: "Communication",
  promise: "Activity",
  status_change: "Activity",
  payment: "Activity",
  system: "Activity",
  case: "Activity",
  dispute: "Activity",
};

const ACTIVITY_TYPE_TO_TYPE: Record<string, PromiseUnifiedActivity["type"]> = {
  email: "email",
  phone: "call",
  sms: "sms",
  communication: "communication",
  note: "note",
  promise: "promise",
  payment: "payment",
  status_change: "status_change",
  system: "system",
  case: "case_note",
  dispute: "dispute",
};

function mapApiToUnifiedActivity(
  item: RecentActivityListItemApiResponse
): PromiseUnifiedActivity {
  const tag = ACTIVITY_TYPE_TO_TAG[item.activityType] ?? "Activity";
  const type = ACTIVITY_TYPE_TO_TYPE[item.activityType] ?? "system";

  let outcome: string | undefined;
  if (item.metadataJson) {
    try {
      const metadata = JSON.parse(item.metadataJson) as Record<string, unknown>;
      if (typeof metadata.outcome === "string") {
        outcome = metadata.outcome;
      }
    } catch {
      // ignore malformed metadata
    }
  }

  return {
    id: String(item.id),
    type,
    tag,
    title: item.title,
    description: item.description,
    outcome,
    createdBy: item.actorName,
    createdAt: new Date(item.createdAt),
  };
}

function buildMockActivities(): PromiseUnifiedActivity[] {
  const items: PromiseUnifiedActivity[] = [
    {
      id: "mock-action-1",
      type: "status_change",
      tag: "Activity",
      title: "Promise status changed to Active",
      description: "Promise status changed to Active",
      createdBy: "Sarah Johnson",
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    },
    {
      id: "mock-action-2",
      type: "call",
      tag: "Communication",
      title: "Outbound call made to customer",
      description: "Outbound call made to customer",
      outcome: "Customer confirmed payment commitment",
      createdBy: "Sarah Johnson",
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    },
    {
      id: "mock-action-3",
      type: "email",
      tag: "Communication",
      title: "Payment reminder email sent",
      description: "Payment reminder email sent",
      outcome: "Email delivered successfully",
      createdBy: "System",
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    },
    {
      id: "mock-note-1",
      type: "note",
      tag: "Note",
      title: "Note Added",
      description:
        "Initial promise recorded via phone call. Customer confirmed payment date.",
      createdBy: "Sarah Johnson",
      createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
    },
    {
      id: "mock-note-2",
      type: "note",
      tag: "Note",
      title: "Note Added",
      description:
        "Follow-up call made. Customer reconfirmed commitment to pay on promised date.",
      createdBy: "Sarah Johnson",
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    },
    {
      id: "mock-note-3",
      type: "note",
      tag: "Note",
      title: "Note Added",
      description:
        "Reminder email sent to customer with payment details and invoice copies.",
      createdBy: "System",
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    },
  ];
  return items.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export function usePromiseRecentActivities(promiseId: string | undefined) {
  const { apiGet } = useApi();
  const mockActivities = buildMockActivities();

  return useModeAwareQuery<PromiseUnifiedActivity[], Error>(
    ["promise-recent-activities", promiseId],
    async ({ signal }) => {
      if (!promiseId) return [];
      try {
        const response = await apiGet<GetRecentActivitiesApiResponse>(
          `/api/v1/recent-activities/promises/${promiseId}`,
          { page: 1, pageSize: 50 },
          signal
        );
        return response.data
          .map(mapApiToUnifiedActivity)
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      } catch (error) {
        console.error("Error fetching promise recent activities:", error);
        return [];
      }
    },
    mockActivities,
    {
      enabled: !!promiseId,
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}
